/**
 * LMV ray adapters — ported from PSET makeRayCaster / classifyByName.
 */
import type { CastRayFn, RayHitKind } from "./types";

export function classifyByNodeName(
  nameOf: (dbId: number) => string
): (dbId: number) => RayHitKind {
  return dbId => {
    const n = nameOf(dbId);
    if (/wall/i.test(n)) return "boundary";
    if (/door/i.test(n)) return "opening";
    return "obstacle";
  };
}

export function makeRayCaster(
  viewer: Autodesk.Viewing.GuiViewer3D,
  classify: (dbId: number) => RayHitKind
): CastRayFn {
  return (from, dir) => {
    const ray = new THREE.Ray(from.clone(), dir);
    const h = viewer.impl.rayIntersect(ray, false, undefined, undefined);
    if (!h || h.dbId == null) return null;
    return {
      point: h.intersectPoint.clone(),
      kind: classify(h.dbId),
    };
  };
}

export async function stageGeometry(
  viewer: Autodesk.Viewing.GuiViewer3D,
  bbox: THREE.Box3,
  waitMs: number,
  unitScale: number
): Promise<void> {
  const c = bbox.getCenter(new THREE.Vector3());
  const nav = viewer.navigation;
  nav.setView(
    new THREE.Vector3(
      c.x + 8 / unitScale,
      c.y - 8 / unitScale,
      c.z + 8 / unitScale
    ),
    c
  );
  nav.setCameraUpVector(new THREE.Vector3(0, 0, 1));
  if (waitMs > 0) {
    await new Promise<void>(r => setTimeout(r, waitMs));
  }
}

/** One-time BVH warm-up at demo start — avoids per-element camera jumps. */
export async function stageModelForRaycast(
  viewer: Autodesk.Viewing.GuiViewer3D,
  waitMs = 1200
): Promise<void> {
  const model = viewer.model;
  const bbox = model?.getBoundingBox?.();
  if (!bbox) return;
  const unitScale = model.getUnitScale?.() ?? 1;
  await stageGeometry(viewer, bbox, waitMs, unitScale);
}

function sleep(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

export { sleep };

export function elementWorldBox(
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number
): THREE.Box3 | null {
  const model = viewer.model;
  const tree = model.getInstanceTree();
  if (!tree) return null;
  const fragList = model.getFragmentList();
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  let empty = true;
  tree.enumNodeFragments(
    dbId,
    fragId => {
      fragList.getWorldBounds(fragId, tmp);
      box.union(tmp);
      empty = false;
    },
    true
  );
  // Fragment bounds may be empty before geometry streams — fall back to
  // instance-tree metadata (PSET DiscoverExtension._unionWorldBox pattern).
  if (empty && tree.getNodeBox) {
    const b = new Float32Array(6);
    try {
      tree.getNodeBox(dbId, b);
      if (b[0] <= b[3]) {
        box.union(
          new THREE.Box3(
            new THREE.Vector3(b[0], b[1], b[2]),
            new THREE.Vector3(b[3], b[4], b[5])
          )
        );
        empty = false;
      }
    } catch {
      /* getNodeBox unavailable for this dbId */
    }
  }
  return empty ? null : box;
}

/** Keep overlay footprints inside visible model geometry (rooms often overshoot walls). */
export function clampBoxToModelShell(
  viewer: Autodesk.Viewing.GuiViewer3D,
  box: THREE.Box3
): THREE.Box3 {
  const modelBox = viewer.model?.getBoundingBox?.();
  if (!modelBox) return box.clone();

  const clamped = box.clone();
  clamped.min.x = Math.max(clamped.min.x, modelBox.min.x);
  clamped.min.y = Math.max(clamped.min.y, modelBox.min.y);
  clamped.max.x = Math.min(clamped.max.x, modelBox.max.x);
  clamped.max.y = Math.min(clamped.max.y, modelBox.max.y);
  clamped.min.z = Math.max(clamped.min.z, modelBox.min.z);
  clamped.max.z = Math.min(clamped.max.z, modelBox.max.z);

  if (clamped.min.x >= clamped.max.x || clamped.min.y >= clamped.max.y) {
    return box.clone();
  }
  return clamped;
}
